package com.oasisfeng.invaluable.event

import com.oasisfeng.invaluable.data.LinkProjection
import com.oasisfeng.invaluable.data.MemoizingLinkRecordResolver
import com.oasisfeng.invaluable.event.ReplicatedEventApplier.ApplyResult

/**
 * DSL-agnostic replicated-event node: the grow-only event sets plus an intake that tolerates any delivery order.
 *
 * This is the substrate **every peer shares**, independent of any domain DSL. It holds the two replicated object sets
 * ([store]), applies incoming events through a [ReplicatedEventApplier] with a bounded out-of-order re-drive buffer, and
 * offers a portable whole-store wire form ([encodeAll] / [importLines]) plus the incremental one persistence actually
 * wants ([encodeSince]). The default store is transient memory; a host may inject a native durable implementation and
 * bypass snapshot replay. Local and remote events flow through the same
 * [apply] intake, so a freshly authored event is observed exactly as a remote peer would observe it.
 *
 * A domain node builds local authoring and a view on top of this (app-social's `SocialNode` extends it); an
 * always-online relay (`:p2p:relay`'s `RelayNode`) needs only this generic core plus
 * [ReplicatedEventSync][com.oasisfeng.invaluable.event] — the forward-on-advance gossip every peer runs — to relay.
 *
 * Not thread-safe: confine to one thread or synchronize externally.
 */
open class ReplicatedEventNode(
    val store: ReplicatedEventStore = InMemoryReplicatedEventStore(Sha256LinkProjector()),
    /** Upper bound on buffered out-of-order attestations; injectable so tests can drive eviction without 4096 events. */
    private val maxPending: Int = DEFAULT_MAX_PENDING,
) {
    /** Projection scheme this node persists and replicates under. */
    val projector = store.projector
    private val applier = ReplicatedEventApplier(store)
    private val batchCodec = BatchWireCodec(projector)
    private val importStager = batchCodec.Stager()

    /**
     * Notified with the events produced by each local authoring action (preimages first, attestation last).
     *
     * The replication layer ([ReplicatedEventSync][com.oasisfeng.invaluable.event]) subscribes to gossip these to
     * peers, so live authoring propagates as a small delta rather than a full-state re-push. Remote events do not flow
     * through here — they arrive via [apply]. A pure relay authors nothing, so this stays the no-op default for it.
     */
    var onLocalEvents: (List<ReplicatedEvent>) -> Unit = {}

    /**
     * Attestations waiting on a preimage that has not arrived yet, keyed by the missing projection.
     *
     * Bounded by [maxPending]: a preimage that never arrives (e.g. a permanently withheld ancestor body — see the
     * selective-reveal granularity constraint in roadmap §7.1) must not grow this buffer without limit. This is only a
     * re-drive cache, never the source of truth, so eviction is safe — a dropped attestation is recovered the next time
     * the sync layer re-pushes state. [mutableMapOf] preserves insertion order, so eviction is oldest-first.
     */
    private val pendingByMissing = mutableMapOf<LinkProjection, MutableList<SignedCommitment>>()
    private var pendingCount = 0

    // ───── replication intake (local and remote, uniform) ─────

    /** Apply one replicated event, re-driving attestations that were waiting on a newly-arrived preimage. */
    open fun apply(event: ReplicatedEvent): ApplyResult {
        val result = applier.apply(event)
        when (event) {
            is LinkPreimage -> if (result is ApplyResult.Applied) onApplied(event)
            is SignedCommitment ->
                if (result is ApplyResult.Applied) onApplied(event)
                else if (result is ApplyResult.Pending) result.missing.forEach { bufferPending(it, event) }
            is SealedContent -> if (result is ApplyResult.Applied) onApplied(event)
        }
        return result
    }

    /**
     * Commit a batch [validateReplicatedEventBatch] has already judged acceptable, returning what genuinely entered.
     *
     * The judging is what costs: resolving the issuer credential, verifying the signature, attributing the head. That
     * has happened, against this very store, in the step that produced [validated] — so this only appends, which is what
     * the overlay could not do because it maintains none of this store's indexes. Re-running the applier here would buy
     * nothing and pay the verification a second time; on a real catch-up that second pass was the single largest cost
     * on both sides of the wire.
     *
     * [validated] must have been produced against **this** store with nothing applied in between — the two form one
     * step, which is also what makes adopting its resolutions sound. Anything else has not been judged and must go
     * through [apply] instead. A batch that validated cannot contain a pending attestation (validation rejects the whole
     * batch on the first one), so nothing here needs the out-of-order buffer; arrivals still re-drive whatever was
     * parked earlier, via [onApplied].
     */
    fun commitValidated(validated: BatchValidationResult.Valid): List<ReplicatedEvent> {
        val admitted = admit(validated.accepted)
        // Only now — with those records durable here — are the validation's resolutions facts about this store rather
        // than about a batch it was still considering. Adopting them is what keeps the *next* batch from rebuilding this
        // chain's ancestry from scratch; the second applier pass used to do it as a side effect of re-verifying.
        (store.resolver() as? MemoizingLinkRecordResolver)?.adopt(validated.resolutions)
        return admitted
    }

    /**
     * Admit a commitment **this node just authored**, returning what genuinely entered the store.
     *
     * A signer does not verify its own fresh signature. Every check [ReplicatedEventApplier] would run here is already
     * true by construction of what [publishCommitment] just returned:
     *
     * - each preimage came from this store's own projector, so `projectLink(parent, target) == projection` holds because
     *   that call is where the projection came from;
     * - the attestation was signed moments ago with [identity][com.oasisfeng.invaluable.identity.Identity]'s key over
     *   this very head, by a [SignedCommitment.sign] that already required that identity to own the issuer credential;
     * - so the attribution it would check — head issued by the credential's actor — is the relation `sign` derived the
     *   credential *from*.
     *
     * Verifying anyway cost the relay 1.27s of Ed25519 per catch-up, checking its own hand. This is emphatically not a
     * general "trusted event" intake: anything that came off a wire, however plausible, goes through [apply] or
     * [commitValidated]. The parameter type is the guard — a [PublishedCommitment] is what local authoring produces.
     */
    fun commitLocal(published: PublishedCommitment): List<ReplicatedEvent> = admit(published.events())

    /** Insert already-trusted events and run their arrival follow-ups; the shared tail of both commit paths. */
    private fun admit(events: List<ReplicatedEvent>): List<ReplicatedEvent> = events.filter { event ->
        val added = when (event) {
            is LinkPreimage -> store.appendPreimage(event.record)
            is SignedCommitment -> store.appendAttestation(event)
            is SealedContent -> store.appendSealed(event)
        }
        if (added) onApplied(event)
        added
    }

    /**
     * Follow-ups owed once [event] has genuinely entered [store], however it was judged — one at a time or as a batch.
     *
     * Subclasses hook arrivals here rather than by overriding [apply], so a batch commit and a single application run
     * the same follow-ups; overriding [apply] alone would silently skip them for everything that arrives in a batch.
     */
    protected open fun onApplied(event: ReplicatedEvent) {
        if (event is LinkPreimage) drainPending(event.projection)
    }

    /** Apply a batch (e.g. a peer's [export]); ordering is tolerated by [apply]'s pending re-drive. */
    fun import(events: Iterable<ReplicatedEvent>) = events.forEach { apply(it) }

    /** Everything this node holds, preimages first — the naive full-state form persistence and seeding replicate. */
    fun export(): List<ReplicatedEvent> = buildList {
        store.preimages().forEach { add(LinkPreimage(it)) }
        store.attestations().forEach { add(it) }
        store.sealedContents().forEach { add(it) }
    }

    /** Encode the node's entire [export] as mandatory compact batch-v1 wire. */
    fun encodeAll(): List<String> = batchCodec.encode(export(), store.resolver()).flatMap(ReplicatedEventBatch::wireLines)

    /**
     * What a durable copy standing at [cursor] is missing, encoded as batch-v1 wire (see [DurableWrite]).
     *
     * A full [encodeAll] re-derives everything held — it rebuilds each record's Link and re-verifies every regenerable
     * closure against its head — so persisting after each advance costs the whole store every time, which over a log is
     * quadratic. The store only grows, so what a copy is missing is the tail of each insertion-ordered set past where
     * that copy stands, and encoding *that* costs what actually arrived.
     *
     * The tail is still self-contained: a recipe verifies itself against its own head, and an endpoint the tail does not
     * carry (a shared integer node, a parent already in the copy) is materialized through this store's resolver exactly
     * as it is for the deltas the sync layer already ships.
     *
     * An erasure is the one act that unmakes the prefix relation those positions rest on, so a [cursor] taken before one
     * is not a position in the current sets at all — it yields a [DurableWrite.Whole], which is also this design's whole
     * compaction story: forgetting is what rewrites the copy, and nothing else has to.
     */
    fun encodeSince(cursor: DurableCursor): DurableWrite {
        val erasures = store.erasures
        val from = if (erasures == cursor.erasures) cursor else DurableCursor.Empty
        val events = mutableListOf<ReplicatedEvent>()
        val preimages = store.preimages().map(::LinkPreimage).collectAfter(from.preimages, events)
        val attestations = store.attestations().collectAfter(from.attestations, events)
        val sealed = store.sealedContents().collectAfter(from.sealed, events)
        val at = DurableCursor(preimages, attestations, sealed, erasures)
        val lines = batchCodec.encode(events, store.resolver()).flatMap(ReplicatedEventBatch::wireLines)
        return if (from.isEmpty) DurableWrite.Whole(lines.joinToString("\n"), at)
        else DurableWrite.Appended(lines.joinToString("") { "\n$it" }, at)
    }

    /**
     * Where a durable copy stands that holds everything this node currently does.
     *
     * The one honest caller is a host that has just replayed that copy's own contents through [restoreLines]: everything
     * here came from there, so the next [encodeSince] with this cursor writes only what arrived afterwards. It may hold
     * slightly *more* than this node does — a record the replay refused is still in the file — which costs nothing,
     * since the replay refuses it again. Handing this cursor to a copy that was never restored from claims it holds
     * records it has never seen, and an append never revisits them.
     */
    fun cursorAfterRestore(): DurableCursor = DurableCursor(
        store.preimages().count(), store.attestations().count(), store.sealedContents().count(), store.erasures,
    )

    /** Add everything past the first [skip] items to [into]; returns how many there were in all. */
    private fun <T> Sequence<T>.collectAfter(skip: Int, into: MutableList<in T>): Int {
        var seen = 0
        for (item in this) if (seen++ >= skip) into.add(item)
        return seen
    }

    /**
     * Replay durable state **this node wrote itself** with [encodeAll], admitting its attestations without re-verifying.
     *
     * What re-verification would buy here is nothing. A batch's payload carries a SHA-256 digest that
     * [BatchWireCodec.Stager] checks twice before any of this runs — against the `END` marker and against the payload it
     * actually read — so corruption and truncation are already caught, by the cheap check rather than the expensive one.
     * Past that, verifying an attestation re-establishes a fact this node established when it first accepted the event,
     * and then wrote to its own disk. An attacker able to edit that file can equally edit the code that checks it, or the
     * private key beside it; the signature is not what stands between them and this store.
     *
     * Preimages **are** still self-verified, because the trade is different: it costs a projection each rather than an
     * Ed25519 verification, and it catches something a signature never would — a record this node wrote but cannot
     * reproduce, i.e. a bug in its own encoder. On a real 2.4MB store the attestation half of this was 5.15s of boot.
     *
     * Strictly for a node's own persisted state. Anything from anywhere else — a peer, a pasted console line, an
     * imported backup of unknown origin — must go through [importLines], which judges it in full.
     *
     * A host replaying a whole copy takes [cursorAfterRestore] afterwards, so the next write extends that copy rather
     * than rewriting it.
     */
    fun restoreLines(lines: Iterable<String>) {
        for (line in lines) when (val staged = importStager.accept(line)) {
            is BatchWireCodec.StageResult.Complete -> restoreBatch(staged.batch)
            is BatchWireCodec.StageResult.Violation -> importStager.discard()
            is BatchWireCodec.StageResult.NotBatch, is BatchWireCodec.StageResult.Pending -> Unit
        }
    }

    /** Preimages first, so an attestation's arrival follow-up sees the closure that was persisted alongside it. */
    private fun restoreBatch(batch: ReplicatedEventBatch) {
        for (event in batch.events.sortedBy { if (it is LinkPreimage) 0 else 1 }) {
            val added = when (event) {
                is LinkPreimage -> applier.apply(event) is ApplyResult.Applied
                is SignedCommitment -> store.appendAttestation(event)
                is SealedContent -> store.appendSealed(event)
            }
            if (added) onApplied(event)
        }
    }

    /**
     * Decode and atomically validate complete batch-v1 units. The stager is retained across calls so line-at-a-time
     * console import remains usable; legacy bare event lines are intentionally ignored after the V4 reset.
     */
    fun importLines(lines: Iterable<String>) {
        for (line in lines) when (val staged = importStager.accept(line)) {
            is BatchWireCodec.StageResult.Complete -> {
                val validated = validateReplicatedEventBatch(staged.batch, store)
                if (validated !is BatchValidationResult.Valid) continue
                commitValidated(validated)
            }
            is BatchWireCodec.StageResult.Violation -> importStager.discard()
            is BatchWireCodec.StageResult.NotBatch, is BatchWireCodec.StageResult.Pending -> Unit
        }
    }

    private fun bufferPending(missing: LinkProjection, attestation: SignedCommitment) {
        pendingByMissing.getOrPut(missing) { mutableListOf() }.add(attestation)
        if (++pendingCount > maxPending) evictOldestPending()
    }

    /** Drop the oldest buffered attestation when the cache is over budget; recovered later by a full-state re-push. */
    private fun evictOldestPending() {
        val oldest = pendingByMissing.entries.firstOrNull() ?: return
        oldest.value.removeAt(0)
        pendingCount--
        if (oldest.value.isEmpty()) pendingByMissing.remove(oldest.key)
    }

    private fun drainPending(arrived: LinkProjection) {
        val waiting = pendingByMissing.remove(arrived) ?: return
        pendingCount -= waiting.size
        for (attestation in waiting) apply(attestation)
    }

    companion object {
        /** Upper bound on buffered out-of-order attestations; a re-drive cache, evicted oldest-first when exceeded. */
        const val DEFAULT_MAX_PENDING = 4096
    }
}
